import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import useClassNames from 'helpers/hooks/useClassNames';
import Image from 'frontastic/lib/image';
import DesktopImageMagnifier from './desktop-image-magnifier';
import MobileImageMagnifier from './mobile-image-magnifier';
import { ProductDetailsProps } from '../product/product-details';
import Magnifier from './magnifier';
import { Product, Variant } from 'shared/types/product';
import Typography from 'components/commercetools-ui/atoms/typography';
import sale from 'public/locales/en/product.json';
import { promotionCategory } from '../product/product-details/types';
import RightIcon from 'public/icons/ag-icons/chev-right.svg';
import LeftIcon from 'public/icons/ag-icons/chev-left.svg';
import { useCookies } from 'react-cookie';
import { ButtonBlock, CartDiscount, productSlug } from 'shared/types/product/Product';
import Link from 'components/commercetools-ui/atoms/link';
import { BreadCrumb } from 'shared/types/content/BreadCrumb';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import PlayButton from 'public/images/Stroke.svg';

interface GalleryProps {
  images: Array<string>;
  productLogo?: string;
  inModalVersion?: ProductDetailsProps['inModalVersion'];
  product?: Product;
  variant?: Variant;
  hoveredImage?: string | null;
  breadCrumb?: BreadCrumb[];
  viewStandardFitOptions?: ButtonBlock[];
}
const PDP_IMAGE_ZOOM_PROPS = {
  magnifierSize: 166,
};

const Gallery: FC<GalleryProps> = ({
  images,
  productLogo,
  inModalVersion,
  product,
  variant,
  hoveredImage,
  breadCrumb,
  viewStandardFitOptions,
}) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [startIndex, setStartIndex] = useState(0);
  const displayedImage = hoveredImage || images[activeSlide];
  const promotionCategory = product?.categories as promotionCategory[] | undefined;
  const visibleItems = 6;
  const titleClassName = useClassNames(['break-normal mb-12', { 'md:text-24': !inModalVersion }]);

  const saleCategory = promotionCategory?.some((i: promotionCategory) => i?.slug == sale?.sale?.slug);
  const agExclusive = promotionCategory?.some((i: promotionCategory) => i?.slug == sale['AG-Exclusive'].slug);
  const newArrivals = promotionCategory?.find((i: promotionCategory) => i?.name === 'New Arrivals');
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const [discountedPrices, setDiscountedPrices] = useState<any>(null);
  const [price, setPrice] = useState<number>(0);

  const discountCategory = promotionCategory?.find(
    (category: promotionCategory) => category.name && /\d+%/.test(category.name),
  );

  const discountPercent = discountCategory?.name.match(/(\d+)%/)?.[1];

  const discountPromo = discountPercent ? `+Extra ${discountPercent}% Off At Checkout` : '';

  const discountUrl = discountCategory?.slug ?? '';
  const cartDiscount = (() => {
    const discount = variant?.attributes?.cartDiscount && JSON.parse(variant?.attributes?.cartDiscount as string);
    return Array.isArray(discount) ? discount : discount ? [discount] : [];
  })() as CartDiscount[];

  // const discountPercentage =
  //   variant?.discountedPrice &&
  //   (((variant?.price?.centAmount as number) - (variant?.discountedPrice.centAmount as number)) /
  //     (variant?.price?.centAmount as number)) *
  //     100;

  const discountPercentage = useMemo(() => {
    if (!discountedPrices || !discountedPrices?.mrrp?.centAmount || !price) {
      return null; // Return null if required data is missing
    }

    const mrrp = discountedPrices?.mrrp?.centAmount;
    const wasPrice = discountedPrices?.wasPrice?.centAmount;

    if (mrrp <= 0 || price <= 0 || mrrp === price) {
      return 0; // No discount if prices are invalid or no actual discount
    }

    const discount = ((mrrp - price) / mrrp) * 100;
    return Math.round(discount); // Round to the nearest integer
  }, [discountedPrices]);

  const handleLeftClick = () => {
    const newIndex = activeSlide === 0 ? images.length - 1 : activeSlide - 1;
    setActiveSlide(newIndex);

    if (newIndex < startIndex) {
      setStartIndex(Math.max(0, startIndex - 1));
    }
  };

  const handleRightClick = () => {
    const newIndex = activeSlide === images.length - 1 ? 0 : activeSlide + 1;
    setActiveSlide(newIndex);

    if (newIndex >= startIndex + visibleItems) {
      setStartIndex(Math.min(images.length - visibleItems, startIndex + 1));
    }
  };

  const handleThumbnailHover = (index: number) => {
    setActiveSlide(index);
  };

  const imagesContainerClassName = useClassNames([
    'relative lg:mx-auto',
    inModalVersion ? 'h-[400px] max-w-[340px]' : '',
  ]);
  useEffect(() => {
    if (variant?.attributes?.standalonePrice) {
      try {
        const standalonePrice = JSON.parse(variant?.attributes?.standalonePrice);
        const selectedPrice =
          standalonePrice?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']?.value;
        const discountedPrices =
          standalonePrice?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']?.custom || null;
        if (selectedPrice) {
          setPrice(selectedPrice?.centAmount);
        }
        if (discountedPrices) {
          setDiscountedPrices(discountedPrices);
        } else {
          setDiscountedPrices(null);
        }
      } catch (error) {
        console.error('Error parsing standalonePrice:', error);
      }
    }
  }, [variant, cookies?.locale_selection?.currency]);

  if (images?.length === 0) {
    return (
      <div className={imagesContainerClassName}>
        <Image src={productLogo} suffix="large" style={{ objectFit: 'contain' }} fill />
      </div>
    );
  }
  const BrandLogo = `https://${product?.brandLogo?.BrandImage?.endpoint}.a.bigcontent.io/v1/static/${product?.brandLogo?.BrandImage?.name}`;
  return (
    <>
      <div className="block sm:hidden">
        <div className="flex flex-col gap-y-4">
          {product?.brandLogo && (
            <div className="w-[100%] max-w-[150px]">
              <img src={BrandLogo} className="w-[100%]" />
            </div>
          )}
          <Typography as="h1" className={`${titleClassName} text-start text-20 font-semibold leading-6`}>
            {product?.name}
          </Typography>
        </div>
        <div className="mb-20 flex gap-x-8">
          {saleCategory && (
            <a href={sale.sale.url}>
              <div className="flex h-[26px] min-w-[56px] cursor-pointer items-center justify-center rounded-md bg-[#E30616] p-7 text-12 font-medium leading-3 text-white">
                {sale?.sale?.name}
              </div>
            </a>
          )}
          {agExclusive && (
            <a href={sale['AG-Exclusive'].url}>
              <div className="flex h-[26px] min-w-[100px] cursor-pointer items-center justify-center rounded-md bg-black p-7 text-12 font-medium leading-3 text-white">
                {sale['AG-Exclusive']?.name}
              </div>
            </a>
          )}
          {newArrivals && (
            <a href={`/${newArrivals?.slug}`}>
              <div className="flex h-[26px] min-w-[59px] cursor-pointer items-center justify-center rounded-md bg-[#127E57] p-7 font-body text-12 font-medium capitalize leading-3 text-white">
                {newArrivals?.slug}
              </div>
            </a>
          )}
        </div>

        {/* As of commentted : Need to fix : DiscountText Promo */}
        {/* {product?.discountTextInfo?.discountText && (
          <div className="inline-flex cursor-pointer items-center justify-center bg-green-100 p-2 px-4 py-2 text-16 font-medium leading-7">
            {product?.discountTextInfo?.discountText}
          </div>
        )} */}

        {cartDiscount?.length > 0 && (
          <div className="flex flex-wrap gap-4">
            {cartDiscount?.map((i: CartDiscount, index: number) => {
              if (i?.description?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']) {
                return (
                  // <Link
                  //   key={index}
                  //   link={`${breadCrumb?.[breadCrumb?.length - 1]?.href}shop-by/offer/${i?.description?.['en-GB']
                  //     .replace(/\s+/g, '-')
                  //     .replace('€', '€')}`}
                  // >
                  <SmartLink
                    key={index}
                    href={`${breadCrumb?.[breadCrumb?.length - 1]?.href}shop-by/offer/${i?.description?.[
                      cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU'
                    ]
                      ?.replace(/\s+/g, '-')
                      ?.replace('€', '€')}`}
                  >
                    <div
                      className={`inline-flex h-[26px] min-w-[86px] cursor-pointer items-center justify-center rounded-sm bg-[#127E57] p-2 px-4 py-4 text-12 font-medium capitalize leading-[12px]  text-white ${
                        index < cartDiscount?.length - 1 ? 'mr-4' : ''
                      }`}
                      // onClick={handleScroll}
                    >
                      {i?.description?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']}
                    </div>
                  </SmartLink>
                  // </Link>
                );
              }
            })}
          </div>
        )}
        {Array.isArray(viewStandardFitOptions) &&
          Array.isArray(product?.popupData?.productSlug) &&
          product?.popupData?.productSlug?.some((item: productSlug) => item?.slug === product?.slug) && (
            <div className="View-Standard-options">
              {viewStandardFitOptions?.map((optionBlock: any, idx: number) => {
                const isCustomFit = product?.variants?.[0]?.attributes?.isCustomFit;

                return Array.isArray(optionBlock?.buttons)
                  ? optionBlock?.buttons
                      ?.filter((btn: any) => btn?.isCustomFit === isCustomFit)
                      ?.map((btn: any, btnIdx: number) => {
                        const matchingSlug = product?.popupData?.productSlug?.find(
                          (item: productSlug) => item?.slug === product?.slug,
                        );

                        return (
                          <SmartLink showLoader={false} href={matchingSlug?.slugLink || ''} key={`${idx}-${btnIdx}`}>
                            <div className="relative w-fit">
                              <button
                                className="flex h-[32px] min-w-[216px] items-center justify-between rounded-[4px] pl-10 pr-10 font-body font-medium"
                                style={{
                                  backgroundColor: btn?.buttonColour,
                                  color: btn?.TextColour,
                                  fontSize: btn?.TextFontSize,
                                }}
                              >
                                <span className="w-full">{btn?.buttonText || 'View Standard Fit options'}</span>
                                <span className="ml-10">
                                  <PlayButton />
                                </span>
                              </button>
                            </div>
                          </SmartLink>
                        );
                      })
                  : null;
              })}
            </div>
          )}
      </div>
      <div className="px-4 py-10 lg:hidden">
        {discountPromo && (
          <a href={`/${discountUrl}`}>
            <div className="mb-20 inline-flex w-full cursor-pointer items-center justify-center bg-green-100 p-2 px-4 py-2 text-16 font-medium leading-7">
              {discountPromo}
            </div>
          </a>
        )}
        {/* As of commentted : Need to fix : DiscountText Promo */}
        {/* {product?.discountTextInfo?.discountText && (
          <div className="inline-flex cursor-pointer items-center justify-center bg-green-100 p-2 px-4 py-2 text-16 font-medium leading-7">
            {product?.discountTextInfo?.discountText}
          </div>
        )} */}
      </div>
      <div className="block gap-y-34 px-4 md:mb-50 lg:hidden">
        <div className={`${imagesContainerClassName} relative`}>
          <MobileImageMagnifier src={displayedImage || images?.[activeSlide] || images?.[0] || productLogo} />
          {product?.variants?.[0]?.attributes?.headcoverIncluded && (
            <div className="border-1 border-1 absolute left-8 top-8 z-10 flex h-[23px] min-w-[50px] items-center justify-center rounded-md border border-[#057052] bg-white p-5">
              <p
                className="text-14 font-normal
                 text-[#057052]"
              >
                {'Headcover included'}
              </p>
            </div>
          )}
          {discountPercentage && (
            <div className="absolute left-8 top-40 z-10 flex h-[26px] min-w-[51px] items-center justify-center rounded-md border border-[#1E1E1E] p-8">
              <p
                className="font-body text-14
                 font-medium leading-4 text-black"
              >{`-${discountPercentage}%`}</p>
            </div>
          )}
          {/* {product?.brandLogo && (
            <div className="absolute right-10 top-10 z-10 w-[100%] max-w-[80px]">
              <img src={BrandLogo} className="w-[100%]" />
            </div>
          )} */}
          {!inModalVersion && images.length > 1 && (
            <div className="absolute bottom-15 right-15 flex gap-10">
              <button
                className={`border-1 flex h-32 w-32 items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                  activeSlide === 0 ? 'border-[#E7E7E7]' : 'border-black'
                }`}
                onClick={handleLeftClick}
                disabled={activeSlide === 0}
              >
                <LeftIcon />
              </button>
              <button
                className={`border-1 flex  h-32 w-32 items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                  activeSlide === images.length - 1 ? 'border-[#E7E7E7]' : 'border-black'
                }`}
                onClick={handleRightClick}
                disabled={activeSlide === images.length - 1}
              >
                <RightIcon />
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="hidden w-[100%] max-w-none lg:block ">
        <div className="align-center flex w-[100%] max-w-none justify-end gap-[1rem]">
          {!inModalVersion && (
            <div className="flex w-[92px] flex-col items-center gap-y-4">
              <div className="flex max-h-[550px] flex-col gap-y-4 overflow-hidden">
                {images?.slice(startIndex, startIndex + visibleItems)?.map((img, index) => (
                  <div
                    key={startIndex + index}
                    className={`relative h-[70px] w-[70px] rounded-md md:h-[85px] md:w-[85px] ${
                      startIndex + index === activeSlide ? 'border border-neutral-500' : ''
                    }`}
                    onMouseEnter={() => handleThumbnailHover(startIndex + index)}
                    onClick={() => setActiveSlide(startIndex + index)}
                  >
                    <div className="relative h-full w-full">
                      <Image src={img || productLogo} suffix="small" className="rounded-md hover:cursor-pointer" fill />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="relative max-w-[740px]">
            {/* Need to Fix Discount Percentage */}
            {/* Zoom percentage display */}
            {product?.variants?.[0]?.attributes?.headcoverIncluded && (
              <div className="absolute left-10 top-10 z-10 flex h-fit min-w-[57px] items-end justify-end rounded-md border border-[#42D6A1] bg-white p-[0.5rem]">
                <p
                  className="font-body text-14 font-medium leading-[100%]
                 text-[#127E57]"
                >
                  {'Headcover included'}
                </p>
              </div>
            )}
            {discountPercentage && (
              <div className="absolute left-10 top-50 z-10 flex h-[26px] min-w-[51px] items-center justify-center rounded-md border border-[#1E1E1E] p-8">
                <p
                  className="font-body text-14
                 font-medium leading-4 text-black"
                >{`-${discountPercentage}%`}</p>
              </div>
            )}
            <Magnifier src={displayedImage || images?.[activeSlide] || images?.[0] || productLogo} />
            {!inModalVersion && images.length > 1 && (
              <div className="absolute bottom-15 right-15 flex gap-10">
                <button
                  className={` border-1 flex h-32 w-32 items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                    activeSlide === 0 ? 'border-[#E7E7E7]' : 'border-black'
                  }`}
                  onClick={handleLeftClick}
                  disabled={activeSlide === 0}
                >
                  <LeftIcon />
                </button>
                <button
                  className={`border-1 flex h-32 w-32 items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                    activeSlide === images.length - 1 ? 'border-[#E7E7E7]' : 'border-black'
                  }`}
                  onClick={handleRightClick}
                  disabled={activeSlide === images.length - 1}
                >
                  <RightIcon />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Gallery;
